import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  PackagePlus,
  Refrigerator,
  Sparkles,
  Truck,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import LocationHeader from "@/components/LocationHeader";
import LocationAccessGate from "@/components/LocationAccessGate";
import WeekSelector from "@/components/WeekSelector";
import { getFingerprint } from "@/lib/fingerprint";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const restockSessionKey = (slug) => `munchd-restock-code:${slug}`;

export default function FridgeLayoutPage() {
  const { slug } = useParams();
  const [locationInfo, setLocationInfo] = useState(null);
  const [accessCode, setAccessCode] = useState(() => localStorage.getItem(`munchd-loc-code:${slug}`));
  const [verifying, setVerifying] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [selectedWeek, setSelectedWeek] = useState(null);
  const [layout, setLayout] = useState(null);
  const [loading, setLoading] = useState(true);

  const [restockCode, setRestockCode] = useState(() => sessionStorage.getItem(restockSessionKey(slug)));
  const [restockMode, setRestockMode] = useState(false);
  const [showRestockGate, setShowRestockGate] = useState(false);

  const [selectedItemId, setSelectedItemId] = useState(null);
  const [pickedUp, setPickedUp] = useState(null); // {id, name, category_emoji} when moving
  const [activeFridgeId, setActiveFridgeId] = useState(null);

  // 1. Public location info (for the gate UI)
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/locations/${slug}/info`)
      .then((res) => !cancelled && setLocationInfo(res.data))
      .catch(() => !cancelled && setNotFound(true))
      .finally(() => !cancelled && setVerifying(false));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // 2. Layout fetch (gated). Always include week.
  const loadLayout = useCallback(async () => {
    if (!accessCode || !selectedWeek) return;
    setLoading(true);
    const useRestock = restockMode && restockCode;
    const url = useRestock
      ? `${API}/locations/${slug}/restock-layout?week=${selectedWeek.week_number}`
      : `${API}/locations/${slug}/fridge-layout?week=${selectedWeek.week_number}`;
    const headers = {
      "X-Location-Code": accessCode,
      "X-User-Fingerprint": getFingerprint(),
    };
    if (useRestock) headers["X-Restock-Code"] = restockCode;
    try {
      const res = await axios.get(url, { headers });
      setLayout(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        if (useRestock) {
          // Restock code rejected — clear and back to customer view
          sessionStorage.removeItem(restockSessionKey(slug));
          setRestockCode(null);
          setRestockMode(false);
          toast.error("Restock password rejected. Please re-enter.");
        } else {
          localStorage.removeItem(`munchd-loc-code:${slug}`);
          setAccessCode(null);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [accessCode, restockCode, restockMode, selectedWeek, slug]);

  useEffect(() => {
    if (accessCode && locationInfo && selectedWeek) loadLayout();
  }, [accessCode, locationInfo, selectedWeek, restockMode, loadLayout]);

  // Build flat item lookup
  const itemsById = useMemo(() => {
    const map = new Map();
    layout?.fridges?.forEach((f) =>
      f.shelves.forEach((s) => s.items.forEach((it) => map.set(it.id, it)))
    );
    layout?.unallocated?.forEach((it) => map.set(it.id, it));
    return map;
  }, [layout]);

  // Keep activeFridgeId in sync with the layout response
  useEffect(() => {
    const ids = layout?.fridges?.map((f) => f.id) || [];
    if (!ids.length) {
      if (activeFridgeId !== null) setActiveFridgeId(null);
      return;
    }
    if (!activeFridgeId || !ids.includes(activeFridgeId)) {
      setActiveFridgeId(ids[0]);
    }
  }, [layout, activeFridgeId]);

  const activeFridge = useMemo(
    () => layout?.fridges?.find((f) => f.id === activeFridgeId) || layout?.fridges?.[0] || null,
    [layout, activeFridgeId]
  );

  const selectedItem = selectedItemId ? itemsById.get(selectedItemId) : null;

  // --- Restock actions ---
  const handleEnterRestockClick = () => {
    if (restockCode) {
      setRestockMode(true);
    } else {
      setShowRestockGate(true);
    }
  };

  const exitRestock = () => {
    setRestockMode(false);
    setPickedUp(null);
  };

  const handleRestockVerified = (code) => {
    sessionStorage.setItem(restockSessionKey(slug), code);
    setRestockCode(code);
    setShowRestockGate(false);
    setRestockMode(true);
  };

  // Move item to shelf (or unallocated when shelfId === null)
  const moveItemToShelf = async (itemId, targetShelfId) => {
    if (!restockMode || !restockCode || !selectedWeek) return;
    try {
      await axios.put(
        `${API}/locations/${slug}/restock/override`,
        {
          week_number: selectedWeek.week_number,
          item_id: itemId,
          shelf_id: targetShelfId, // null = back to auto (unallocated if no shelf matches)
        },
        {
          headers: {
            "X-Location-Code": accessCode,
            "X-Restock-Code": restockCode,
          },
        }
      );
      toast.success(targetShelfId ? "Item moved" : "Override cleared");
      setPickedUp(null);
      loadLayout();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Move failed");
    }
  };

  // --- Render states ---
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#1aa59a]" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center max-w-sm" data-testid="fridge-location-not-found">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Location not found</h1>
        </div>
      </div>
    );
  }

  if (!accessCode) {
    return (
      <LocationAccessGate
        slug={slug}
        locationInfo={locationInfo}
        onVerified={(code) => setAccessCode(code)}
      />
    );
  }

  const noConfig = !loading && (!layout?.fridges || layout.fridges.length === 0);

  return (
    <div className="min-h-screen bg-slate-50 pb-12" data-testid="fridge-layout-page">
      <LocationHeader location={locationInfo} />

      {/* Sub-header */}
      <div className="max-w-7xl mx-auto px-4 pt-4 flex flex-wrap items-center gap-3">
        <Link
          to={`/loc/${slug}`}
          className="inline-flex items-center text-sm text-gray-600 hover:text-[#1aa59a]"
          data-testid="fridge-back-to-menu"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to menu
        </Link>

        <div className="flex items-center gap-2 ml-auto">
          {restockMode ? (
            <Button
              size="sm"
              variant="outline"
              onClick={exitRestock}
              data-testid="fridge-exit-restock"
            >
              <X className="w-4 h-4 mr-1" /> Exit restock
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={handleEnterRestockClick}
              data-testid="fridge-toggle-restock"
            >
              <Truck className="w-4 h-4 mr-1" /> Restock view
            </Button>
          )}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 mt-4">
        <div className="flex items-center gap-2 mb-2">
          <Refrigerator className="w-6 h-6 text-[#1aa59a]" />
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
            Smart Fridge Layout
            {restockMode && (
              <span className="ml-2 text-xs bg-amber-500 text-white px-2 py-1 rounded-full align-middle">
                RESTOCK MODE
              </span>
            )}
          </h2>
        </div>

        {restockMode && (
          <WeekSelector selectedWeek={selectedWeek} onWeekChange={setSelectedWeek} />
        )}
        {/* Hidden week selector to bootstrap selectedWeek on customer view */}
        {!restockMode && !selectedWeek && (
          <div className="hidden">
            <WeekSelector selectedWeek={selectedWeek} onWeekChange={setSelectedWeek} />
          </div>
        )}

        {pickedUp && (
          <div
            className="mt-3 p-3 rounded-lg bg-amber-100 border-2 border-amber-300 flex items-center gap-3 sticky top-2 z-30"
            data-testid="fridge-picked-up-banner"
          >
            <PackagePlus className="w-5 h-5 text-amber-700" />
            <span className="text-sm text-amber-900">
              Moving <strong>{pickedUp.name}</strong> — tap a shelf to place, or
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => moveItemToShelf(pickedUp.id, null)}
              data-testid="fridge-move-to-unallocated"
            >
              send to Unallocated
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setPickedUp(null)}>
              Cancel
            </Button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#1aa59a]" />
          </div>
        ) : noConfig ? (
          <div
            className="text-center py-16 border-2 border-dashed rounded-xl bg-white"
            data-testid="fridge-no-config"
          >
            <Refrigerator className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 font-semibold mb-1">No fridge layout configured yet</p>
            <p className="text-sm text-gray-400 max-w-sm mx-auto">
              Your office admin hasn&apos;t set up the Smart Fridge for {locationInfo?.name} yet.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 mt-3">
            {/* Fridges column */}
            <div className="space-y-4">
              {/* Multi-fridge tabs — visible only when 2+ fridges */}
              {layout.fridges.length > 1 && (
                <div
                  className="flex flex-wrap gap-2 p-2 bg-white rounded-lg border-2 border-slate-200 shadow-sm"
                  role="tablist"
                  data-testid="fridge-tabs"
                >
                  {layout.fridges.map((f) => {
                    const isActive = f.id === activeFridge?.id;
                    return (
                      <button
                        key={f.id}
                        role="tab"
                        type="button"
                        aria-selected={isActive}
                        onClick={() => setActiveFridgeId(f.id)}
                        className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-md text-sm font-semibold transition-all ${
                          isActive
                            ? "bg-[#1aa59a] text-white shadow"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                        }`}
                        data-testid={`fridge-tab-${f.id}`}
                      >
                        <Refrigerator className="w-4 h-4" />
                        <span>{f.name}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {activeFridge && (
                <FridgeUnit
                  fridge={activeFridge}
                  restockMode={restockMode}
                  selectedItemId={selectedItemId}
                  onSelectItem={setSelectedItemId}
                  pickedUp={pickedUp}
                  onPickUp={(it) => setPickedUp(it)}
                  onPlaceOnShelf={(shelfId) => pickedUp && moveItemToShelf(pickedUp.id, shelfId)}
                />
              )}

              {restockMode && (
                <UnallocatedTray
                  items={layout.unallocated || []}
                  pickedUp={pickedUp}
                  onPickUp={(it) => setPickedUp(it)}
                />
              )}
            </div>

            {/* Detail side panel */}
            <aside className="lg:sticky lg:top-24 self-start">
              <DetailPanel item={selectedItem} restockMode={restockMode} />
            </aside>
          </div>
        )}
      </main>

      {showRestockGate && (
        <RestockPasswordGate
          slug={slug}
          onCancel={() => setShowRestockGate(false)}
          onVerified={handleRestockVerified}
        />
      )}
    </div>
  );
}

// ---------- Sub-components ----------

function FridgeUnit({ fridge, restockMode, selectedItemId, onSelectItem, pickedUp, onPickUp, onPlaceOnShelf }) {
  return (
    <Card
      className="overflow-hidden border-4 border-slate-300 bg-gradient-to-b from-white to-slate-100 shadow-lg"
      data-testid={`fridge-unit-${fridge.id}`}
    >
      <div className="bg-slate-800 text-white px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Refrigerator className="w-4 h-4" />
          <span className="font-semibold text-sm tracking-wide">{fridge.name}</span>
        </div>
        <span className="text-xs text-emerald-300 font-mono">● 4°C</span>
      </div>

      <div className="p-3 sm:p-4 space-y-3">
        {fridge.shelves.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-6">No shelves on this fridge.</p>
        )}
        {fridge.shelves.map((shelf) => (
          <Shelf
            key={shelf.id}
            shelf={shelf}
            restockMode={restockMode}
            selectedItemId={selectedItemId}
            onSelectItem={onSelectItem}
            pickedUp={pickedUp}
            onPickUp={onPickUp}
            onPlace={() => onPlaceOnShelf(shelf.id)}
          />
        ))}
      </div>
    </Card>
  );
}

function Shelf({ shelf, restockMode, selectedItemId, onSelectItem, pickedUp, onPickUp, onPlace }) {
  // In restock mode, show ALL items (no capacity trim). In customer mode, trim to capacity.
  const items = restockMode ? shelf.items : shelf.items.slice(0, shelf.capacity);
  const overflow = restockMode ? 0 : Math.max(0, shelf.items.length - items.length);
  const emptySlots = restockMode ? Math.max(0, shelf.capacity - items.length) : 0;
  const primaryEmoji = shelf.categories?.[0]?.emoji || "•";

  const isDropTarget = restockMode && !!pickedUp;

  return (
    <div
      className={`rounded-md bg-white border border-slate-200 shadow-sm transition-colors ${
        isDropTarget ? "ring-2 ring-amber-400 cursor-pointer" : ""
      }`}
      onClick={isDropTarget ? onPlace : undefined}
      data-testid={`shelf-${shelf.id}`}
    >
      {/* Shelf header — branded gradient, centred, prominent */}
      <div
        className="relative px-3 py-2.5 bg-gradient-to-r from-[#1aa59a] via-[#1aa59a] to-[#158a80] rounded-t-md shadow-sm"
        data-testid={`shelf-header-${shelf.id}`}
      >
        <div className="flex items-center justify-center gap-2">
          <span className="text-xl leading-none drop-shadow-sm">{primaryEmoji}</span>
          <h3 className="text-sm sm:text-base font-bold text-white tracking-wide uppercase text-center">
            {shelf.label || shelf.categories.map((c) => c.label).join(" + ") || "Shelf"}
          </h3>
        </div>
        {restockMode && (
          <span
            className={`absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-mono font-bold px-2 py-0.5 rounded-full ${
              items.length > shelf.capacity
                ? "bg-amber-100 text-amber-700"
                : emptySlots > 0
                ? "bg-red-100 text-red-700"
                : "bg-white text-[#1aa59a]"
            }`}
            data-testid={`shelf-capacity-${shelf.id}`}
          >
            {items.length}/{shelf.capacity}
          </span>
        )}
      </div>

      {/* Product row — flex grow to fill shelf width */}
      <div className="p-2 flex flex-wrap gap-2 min-h-[100px]">
        {items.map((item) => (
          <ProductSlot
            key={item.id}
            item={item}
            selected={selectedItemId === item.id}
            picked={pickedUp?.id === item.id}
            onClick={() => {
              if (restockMode) {
                if (pickedUp) {
                  // Place on this shelf
                  onPlace();
                } else {
                  onPickUp(item);
                }
              } else {
                onSelectItem(item.id);
              }
            }}
            shelfEmoji={shelf.categories.find((c) => c.key === item.category)?.emoji || primaryEmoji}
            restockMode={restockMode}
          />
        ))}

        {restockMode &&
          Array.from({ length: emptySlots }).map((_, i) => (
            <div
              key={`empty-${i}`}
              className="flex-1 min-w-[120px] sm:min-w-[140px] min-h-[88px] sm:min-h-[100px] rounded-md border-2 border-dashed border-red-300 bg-red-50/50 flex items-center justify-center text-[11px] text-red-400 font-semibold"
              data-testid={`empty-slot-${shelf.id}-${i}`}
            >
              EMPTY
            </div>
          ))}

        {!restockMode && overflow > 0 && (
          <div className="flex-1 min-w-[120px] sm:min-w-[140px] min-h-[88px] sm:min-h-[100px] rounded-md bg-slate-100 flex items-center justify-center text-sm text-slate-500 font-semibold">
            +{overflow}
          </div>
        )}

        {items.length === 0 && !restockMode && (
          <p className="text-xs text-gray-400 py-3 px-1">Nothing on this shelf right now.</p>
        )}
      </div>
    </div>
  );
}

function ProductSlot({ item, selected, picked, onClick, shelfEmoji, restockMode }) {
  const showLikes = item.like_count > 0 && !restockMode;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={`relative flex-1 min-w-[120px] sm:min-w-[140px] max-w-[200px] min-h-[88px] sm:min-h-[100px] rounded-md border-2 transition-all p-2 text-left flex flex-col justify-between ${
        picked
          ? "border-amber-500 bg-amber-50 shadow-md scale-[1.03]"
          : selected
          ? "border-[#1aa59a] bg-[#1aa59a]/10 shadow-md scale-[1.03]"
          : "border-slate-200 bg-white hover:border-[#1aa59a]/60"
      }`}
      data-testid={`product-slot-${item.id}`}
    >
      {/* Top row: emoji ・ likes badge ・ price — all inline, no overlap */}
      <div className="flex items-center gap-1 text-[10px] text-slate-500">
        <span className="text-sm leading-none opacity-80 shrink-0">
          {item.category_emoji || shelfEmoji}
        </span>
        {showLikes && (
          <span
            className="bg-rose-500 text-white font-bold rounded-full px-1.5 py-0.5 leading-none flex items-center gap-0.5 shadow-sm shrink-0"
            data-testid={`product-slot-likes-${item.id}`}
          >
            <span>❤</span>
            <span>{item.like_count}</span>
          </span>
        )}
        {!restockMode && (
          <span className="ml-auto font-bold text-[#1aa59a] text-xs shrink-0">
            £{item.price?.toFixed?.(2)}
          </span>
        )}
      </div>

      {/* Dish name — the visual focus */}
      <span
        className="text-sm sm:text-base font-bold text-slate-900 leading-tight line-clamp-2 mt-1"
        data-testid={`product-slot-name-${item.id}`}
      >
        {item.name}
      </span>
    </button>
  );
}

function UnallocatedTray({ items, pickedUp, onPickUp }) {
  return (
    <Card
      className="border-2 border-dashed border-amber-400 bg-amber-50/40 p-3"
      data-testid="fridge-unallocated-tray"
    >
      <div className="flex items-center gap-2 mb-2">
        <PackagePlus className="w-4 h-4 text-amber-700" />
        <h3 className="text-sm font-bold text-amber-900">
          Unallocated ({items.length})
        </h3>
        <span className="text-xs text-amber-700/80">— items not assigned to any shelf</span>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-amber-700/70 py-2">Every available item is allocated to a shelf — well done!</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {items.map((item) => (
            <ProductSlot
              key={item.id}
              item={item}
              picked={pickedUp?.id === item.id}
              onClick={() => onPickUp(item)}
              shelfEmoji={item.category_emoji || "📦"}
              restockMode
            />
          ))}
        </div>
      )}
    </Card>
  );
}

function DetailPanel({ item, restockMode }) {
  if (!item) {
    return (
      <Card className="p-5 text-center bg-white">
        <Sparkles className="w-6 h-6 text-[#1aa59a] mx-auto mb-2" />
        <p className="text-sm text-gray-500">
          {restockMode
            ? "Tap a product to pick it up, then tap a shelf to move it."
            : "Tap a product on a shelf to see details."}
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-5 bg-white" data-testid="fridge-detail-panel">
      <div className="text-4xl mb-2">{item.category_emoji || "🍽️"}</div>
      <h3 className="text-lg font-bold text-gray-900">{item.name}</h3>
      <p className="text-2xl font-bold text-[#1aa59a] mt-1">£{item.price?.toFixed?.(2)}</p>

      <div className="mt-4 space-y-2 text-sm">
        <Row label="Likes" value={`❤ ${item.like_count}`} />
        {item.week_number && <Row label="Week" value={`Week ${item.week_number}`} />}
        <Row label="Status" value={item.is_active ? "Available" : "Hidden"} />
      </div>
    </Card>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b last:border-b-0">
      <span className="text-xs uppercase tracking-wide text-gray-500">{label}</span>
      <span className="text-sm font-semibold text-gray-800">{value}</span>
    </div>
  );
}

function RestockPasswordGate({ slug, onCancel, onVerified }) {
  const [code, setCode] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    if (!/^\d{6}$/.test(code)) {
      setError("Enter the 6-digit restock code.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/locations/${slug}/restock/verify`, {
        password: code,
      });
      if (res.data?.valid) {
        onVerified(code);
      } else {
        setError("Incorrect restock code.");
      }
    } catch {
      setError("Verification failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4" data-testid="restock-gate">
      <Card className="w-full max-w-sm p-6 bg-white">
        <div className="flex items-center gap-2 mb-3">
          <Lock className="w-5 h-5 text-amber-600" />
          <h3 className="text-lg font-bold">Restock Access</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Enter the 6-digit restock code for this location.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <div className="relative">
            <Input
              type={show ? "text" : "password"}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              maxLength={6}
              inputMode="numeric"
              autoFocus
              placeholder="6-digit code"
              className="font-mono tracking-widest pr-9 text-center text-lg"
              data-testid="restock-gate-input"
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
            >
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {error && <p className="text-sm text-red-600" data-testid="restock-gate-error">{error}</p>}
          <div className="flex gap-2">
            <Button type="button" variant="outline" className="flex-1" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="restock-gate-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Unlock"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
